import moment from 'moment';
import { PropTypes } from 'prop-types';
import DataTable from '../../../core/components/DataTable';
import PageActions from '../../../core/components/PageActions';
import TableActionButtonsCell from '../../../core/components/TableActionButtonsCell';
import { PermissionKeys } from '../../../core/components/UserPermissions';
import { formatAmount } from '../../../utils/money-util';
import { formatNumber } from '../../../utils/number-util';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  addPermissionRefs,
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { get, map } from 'lodash';
import { useSelector } from 'react-redux';
import { hasRole } from '../../../core/components/Permission';
import { selectRole } from '../../../store/authReducer';
import VisualComponent from '../../overview/components/VisualComponent';
import { Container, Row, Col } from 'reactstrap';
import { selectDashboard } from '../../analytics/reducer';
import { utilityVisualIds } from '../chartTypes';
import UserGroupPermissions from '../../../core/components/UserGroupPermissions';
import { getApiStatus } from '../../../core/utils';
import { FETCH_UTILITIES } from '../actions';

// Constants
const NAME_MAX_LENGTH = 25;
const NAME_TRUNCATE_LENGTH = 22;
const OPERATOR_ADMIN_ROLE = 'OperatorAdmin';

// Utility type to message ID mapping
const UTILITY_TYPE_MESSAGES = {
  electric: { id: 'addElectricBill', default: 'Add Electric Bill' },
  gas: { id: 'addGasBill', default: 'Add Gas Bill' },
  water: { id: 'addWaterSewerBill', default: 'Add Water/Sewer Bill' },
  sewer: { id: 'addWaterSewerBill', default: 'Add Water/Sewer Bill' },
};

export const UtilityTable = ({
  pageInfo,
  utilities,
  fetchUtilities,
  showDialog,
  getUtility,
  saveUtility,
  deleteUtility,
  permissions,
  pageFilter,
}) => {
  const [currentFilters, setCurrentFilters] = useState({
    utilityType: 'Water',
  });
  const [sortQuery, setSortQuery] = useState(null);
  const intl = useIntl();
  const role = useSelector(selectRole);
  const isLoading = useSelector((state) => getApiStatus(state, FETCH_UTILITIES));

  // Helper to get query parameters
  const getQueryParams = useCallback(
    (page = 0) => {
      return { page, currentFilters, sortQuery };
    },
    [currentFilters, sortQuery]
  );

  // Update filters when pageFilter changes
  useEffect(() => {
    if (pageFilter) {
      setCurrentFilters({ utilityType: pageFilter });
    }
  }, [pageFilter]);

  // Fetch utilities when filters or sort changes
  useEffect(() => {
    fetchUtilities(getQueryParams());
  }, [fetchUtilities, getQueryParams]);

  // Get utility type message configuration
  const getUtilityTypeMessage = useCallback(() => {
    const filterLower = String(pageFilter || '').toLowerCase();
    const type = Object.keys(UTILITY_TYPE_MESSAGES).find((key) => filterLower.includes(key));
    return type
      ? UTILITY_TYPE_MESSAGES[type]
      : { id: 'addUtilityBill', default: 'Add Utility Bill' };
  }, [pageFilter]);

  // Get page actions with memoization
  const pageActions = useMemo(() => {
    const queryParams = getQueryParams();
    const callback = () => fetchUtilities(queryParams);
    const { id: addBillMessageId, default: addBillDefault } = getUtilityTypeMessage();

    return [
      {
        dialogType: 'UPSERT_UTILITY',
        permissionKey: addPermissionRefs.expense,
        text: intl.formatMessage({ id: addBillMessageId, defaultMessage: addBillDefault }),
        dialogProps: {
          isNew: true,
          callback,
          title: intl.formatMessage({ id: addBillMessageId, defaultMessage: addBillDefault }),
          tabType: pageFilter,
        },
      },
    ];
  }, [getQueryParams, fetchUtilities, getUtilityTypeMessage, intl, pageFilter]);
  // Check if user has admin role
  const isOperatorAdmin = useMemo(() => hasRole(role, OPERATOR_ADMIN_ROLE), [role]);

  // Check permissions
  const canDeleteUtils = useMemo(
    () => permissions[PermissionKeys.deleteUtils] || isOperatorAdmin,
    [permissions, isOperatorAdmin]
  );

  const canAddEditUtils = useMemo(
    () => permissions[PermissionKeys.addEditUtils] || isOperatorAdmin,
    [permissions, isOperatorAdmin]
  );

  const hasAnyActionPermission = useMemo(
    () => canDeleteUtils || canAddEditUtils,
    [canDeleteUtils, canAddEditUtils]
  );

  // Edit utility action
  const editUtilityAction = useCallback(
    (event, { _id }) => {
      getUtility(_id);
      const dialogProps = {
        title: intl.formatMessage({ id: 'editUtility' }),
        onSubmit: saveUtility,
        isNew: false,
      };
      showDialog('UPSERT_UTILITY', dialogProps);
    },
    [getUtility, intl, saveUtility, showDialog]
  );

  // Delete utility action
  const deleteUtilityAction = useCallback(
    (event, { _id, name }) => {
      const queryParams = getQueryParams();
      const callback = () => fetchUtilities(queryParams);
      const confirmProps = {
        onReady: () => deleteUtility(_id, callback),
        text: intl.formatMessage({ id: 'archiveUtilityConfirmation' }, { name }),
        title: intl.formatMessage({ id: 'archiveUtility' }),
      };
      showDialog('CONFIRM_DIALOG', confirmProps);
    },
    [getQueryParams, fetchUtilities, deleteUtility, intl, showDialog]
  );
  // Calculate final usage from API or readings
  const calculateFinalUsage = useCallback((utility) => {
    const usageFromApi = Number(utility?.usage);
    const usageFromReadings = Number(utility?.endReading) - Number(utility?.startReading);

    if (Number.isFinite(usageFromApi) && usageFromApi > 0) {
      return usageFromApi;
    }
    if (Number.isFinite(usageFromReadings) && usageFromReadings > 0) {
      return usageFromReadings;
    }
    return null;
  }, []);

  // Calculate cost per unit
  const calculateCostPerUnit = useCallback((utility, finalUsage) => {
    const costNum = Number(utility?.cost);
    const hasValidCpu = Number.isFinite(finalUsage) && finalUsage > 0 && Number.isFinite(costNum);

    if (!hasValidCpu) {
      return '-';
    }

    const costPerUnitValue = formatAmount(costNum / finalUsage, utility.currency, true);
    const unitSuffix = utility?.unit ? ` / ${utility.unit}` : '';
    return `${costPerUnitValue}${unitSuffix}`;
  }, []);

  // Format display name with truncation
  const formatDisplayName = useCallback((name) => {
    if (typeof name === 'string' && name.length > NAME_MAX_LENGTH) {
      return `${name.slice(0, NAME_TRUNCATE_LENGTH)}...`;
    }
    return name;
  }, []);

  // Transform utility data for table
  const getTableData = useMemo(() => {
    return map(utilities, (utility) => {
      const finalUsage = calculateFinalUsage(utility);
      const costPerUnit = calculateCostPerUnit(utility, finalUsage);

      return {
        _id: utility.id,
        name: formatDisplayName(utility.name),
        start: moment(utility.start).format('MM/DD/YYYY'),
        end: moment(utility.end).format('MM/DD/YYYY'),
        startReading: formatNumber(utility.startReading, 4, true),
        endReading: formatNumber(utility.endReading, 4, true),
        cost: formatAmount(utility.cost, utility.currency, true),
        costPerUnit,
      };
    });
  }, [utilities, calculateFinalUsage, calculateCostPerUnit, formatDisplayName]);
  // Handle sorting
  const triggerUtilities = useCallback((sortedQuery) => {
    if (get(sortedQuery, 'key', false)) {
      setSortQuery(sortedQuery);
    }
  }, []);

  // Handle pagination
  const getUtilitiesPage = useCallback(
    (page) => {
      const queryParams = getQueryParams(page);
      fetchUtilities(queryParams);
    },
    [getQueryParams, fetchUtilities]
  );

  // Initialize action button header
  const initializeActionButtonHeader = useMemo(() => {
    const buttons = [];

    if (canDeleteUtils) {
      buttons.push({
        icon: 'icon-trash',
        buttonAction: deleteUtilityAction,
      });
    }
    if (canAddEditUtils) {
      buttons.push({
        icon: 'icon-edit',
        buttonAction: editUtilityAction,
      });
    }

    return {
      name: intl.formatMessage({ id: 'action' }),
      cell: {
        component: TableActionButtonsCell,
        mixIns: {
          buttons,
        },
      },
    };
  }, [intl, canDeleteUtils, canAddEditUtils, deleteUtilityAction, editUtilityAction]);

  // Initialize datatable configuration
  const initializeDatatable = useMemo(() => {
    const headers = [
      { name: intl.formatMessage({ id: 'billDescription' }), databaseProperty: 'name' },
      { name: intl.formatMessage({ id: 'startDate' }), databaseProperty: 'start' },
      { name: intl.formatMessage({ id: 'endDate' }), databaseProperty: 'end' },
      { name: intl.formatMessage({ id: 'startReading' }), databaseProperty: 'startReading' },
      { name: intl.formatMessage({ id: 'endReading' }), databaseProperty: 'endReading' },
      { name: intl.formatMessage({ id: 'amount' }), databaseProperty: 'cost' },
      {
        name: intl.formatMessage({ id: 'costPerUnit', defaultMessage: 'Cost / Unit' }),
        databaseProperty: 'costPerUnit',
      },
    ];

    if (hasAnyActionPermission) {
      headers.push(initializeActionButtonHeader);
    }

    const paginationInfo = {
      ...pageInfo,
      goToPage: getUtilitiesPage,
    };

    return {
      data: getTableData,
      headers,
      paginationInfo,
      scrolling: true,
      fetchSortedData: triggerUtilities,
      isLoading,
      showLoader: isLoading,
    };
  }, [intl, hasAnyActionPermission, pageInfo, getUtilitiesPage, getTableData, triggerUtilities]);

  return (
    <div className="purchase-orders">
      <UserGroupPermissions itemRef={viewPermissionRefs.utility_chart_section}>
        <Container fluid className="overview px-2">
          <Row className="gx-2 gy-2" style={{ marginBottom: 10 }}>
            {utilityVisualIds[String(pageFilter)?.toLowerCase()]?.map(({ id, title }) => (
              <Col xs={12} lg={4} key={id} className="px-1">
                <VisualComponent
                  visualId={id}
                  pageName="ReportSectionde220715d8729a0eb06d"
                  title={title}
                  selectorFn={selectDashboard}
                  dashboardType="utility"
                />
              </Col>
            ))}
          </Row>
        </Container>
      </UserGroupPermissions>
      <div
        className="filters-actions-container"
        style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}
      >
        <PageActions actionFunction={showDialog} actions={pageActions} />
      </div>
      <div className="table-buttons"></div>
      <DataTable {...initializeDatatable} />
    </div>
  );
};

UtilityTable.propTypes = {
  pageInfo: PropTypes.shape({}).isRequired,
  utilities: PropTypes.arrayOf(PropTypes.object),
  fetchUtilityUnits: PropTypes.func.isRequired,
  fetchUtilities: PropTypes.func.isRequired,
  showDialog: PropTypes.func.isRequired,
  getUtility: PropTypes.func.isRequired,
  saveUtility: PropTypes.func.isRequired,
  deleteUtility: PropTypes.func.isRequired,
  filter: PropTypes.string,
  permissions: PropTypes.shape({}),
  intl: PropTypes.shape({}).isRequired,
  total: PropTypes.number,
  page: PropTypes.number,
  pageSize: PropTypes.number,
};

export default UtilityTable;
